import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api";
import { useAuth } from "./AuthContext";
import { useRealtime } from "./useRealtime";
import type { BandSettings } from "./types";

interface Branding {
  bandName: string;

  logoUrl: string | null;        // /api/branding/logo?v=…    when set
  logoUpdatedAt: string;
  logoWidthPx: number;           // 24–64
  logoOpacity: number;           // 0–100; default 100 (fully opaque)

  faviconUrl: string | null;     // /api/branding/favicon?v=… when set
  faviconUpdatedAt: string;

  ogImageUrl: string | null;     // /api/branding/og-image?v=… when set
  ogImageUpdatedAt: string;

  titleSizePx: number;           // 24–64; 40 = natural size

  refresh: () => Promise<void>;
}

const DEFAULT_BAND_NAME = "Brookfield";
const DEFAULT_WIDTH = 40;
/** Slider value that maps to 1.0× scale (natural size) for both logo and title. */
export const SCALE_BASELINE = 40;

const BrandingContext = createContext<Branding | null>(null);

function clampWidth(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.max(24, Math.min(64, Math.round(n)));
}

function clampPercent(n: number): number {
  if (!Number.isFinite(n)) return 100;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function buildUrl(slug: string, key: string, updatedAt: string): string | null {
  if (!key) return null;
  return `/api/branding/${slug}${updatedAt ? `?v=${encodeURIComponent(updatedAt)}` : ""}`;
}

function applyDocumentTitle(bandName: string) {
  document.title = `${bandName} Band`;
}

function applyFavicon(faviconUrl: string | null) {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return;
  link.href = faviconUrl ?? "/favicon.png";
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [bandName,            setBandName]            = useState(DEFAULT_BAND_NAME);

  const [logoR2Key,           setLogoR2Key]           = useState("");
  const [logoUpdatedAt,       setLogoUpdatedAt]       = useState("");
  const [logoWidthPx,         setLogoWidthPx]         = useState(DEFAULT_WIDTH);
  const [logoOpacity,         setLogoOpacity]         = useState(100);

  const [faviconR2Key,        setFaviconR2Key]        = useState("");
  const [faviconUpdatedAt,    setFaviconUpdatedAt]    = useState("");

  const [ogImageR2Key,        setOgImageR2Key]        = useState("");
  const [ogImageUpdatedAt,    setOgImageUpdatedAt]    = useState("");

  const [titleSizePx,         setTitleSizePx]         = useState(SCALE_BASELINE);

  const refresh = useCallback(async () => {
    const res = await api.get<BandSettings>("/settings");
    if (!res.ok) return;
    const d = res.data;
    setBandName(d.band_name || DEFAULT_BAND_NAME);
    setLogoR2Key(d.logo_r2_key || "");
    setLogoUpdatedAt(d.logo_updated_at || "");
    setLogoWidthPx(clampWidth(d.logo_width_px));
    setLogoOpacity(clampPercent(d.logo_opacity));
    setFaviconR2Key(d.favicon_r2_key || "");
    setFaviconUpdatedAt(d.favicon_updated_at || "");
    setOgImageR2Key(d.og_image_r2_key || "");
    setOgImageUpdatedAt(d.og_image_updated_at || "");
    setTitleSizePx(clampWidth(d.title_size_px));
  }, []);

  useEffect(() => {
    if (!user) return;
    void refresh();
  }, [user, refresh]);

  useRealtime("branding:updated", () => { void refresh(); });

  const logoUrl    = buildUrl("logo",     logoR2Key,    logoUpdatedAt);
  const faviconUrl = buildUrl("favicon",  faviconR2Key, faviconUpdatedAt);
  const ogImageUrl = buildUrl("og-image", ogImageR2Key, ogImageUpdatedAt);

  useEffect(() => { applyDocumentTitle(bandName); }, [bandName]);
  useEffect(() => { applyFavicon(faviconUrl);     }, [faviconUrl]);

  const value: Branding = {
    bandName,
    logoUrl, logoUpdatedAt, logoWidthPx, logoOpacity,
    faviconUrl, faviconUpdatedAt,
    ogImageUrl, ogImageUpdatedAt,
    titleSizePx,
    refresh,
  };

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): Branding {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error("useBranding must be used within BrandingProvider");
  return ctx;
}
